import { ChevronLeft, PanelLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUiStore } from '@/stores/uiStore';

interface TopbarProps {
  /** Page heading rendered next to the back button. */
  title: string;
  /** Optional line under the heading. Pages that don't pass one are unchanged —
   *  the element is not rendered at all, so no page gains blank space. */
  subtitle?: string;
  /**
   * The page's primary controls — a search box, a "New …" button — rendered at the right-hand end of
   * THIS row.
   *
   * WHY THEY BELONG HERE. Both list pages used to print their heading twice: once here from
   * `AppShell title`, and again as an `<h1>` in the page body next to its description and its action
   * button, because that is where the designer puts the group. Two headings, one of them a duplicate,
   * and about 90px of vertical space spent on the repetition. Passing the description and the actions
   * up means one row carries the whole group — the designer's layout — and the body starts at the
   * table.
   */
  actions?: ReactNode;
  /**
   * Where the back chevron goes. Provide it ONLY on a page that sits inside another —
   * a session's detail belongs to the sessions list, so it passes "/sessions".
   *
   * Omitted on a top-level page, and then no chevron renders at all. That matters: this
   * chevron used to be the sidebar's expand toggle, so a left-arrow appeared beside the
   * page title on the LIST page and expanded the rail when clicked. A left-chevron next
   * to a heading means "go back" everywhere else in the product, so it now does exactly
   * that, and the rail is collapsed and expanded from the one button on the rail itself.
   */
  backTo?: string;
}

// Sticky bar above the content area:
//   * Back chevron — only when the page passes `backTo`.
//   * Mobile sidebar toggle (PanelLeft) — visible <lg; opens the drawer.
//
// NO NOTIFICATIONS BELL AND NO PROFILE AVATAR. Both used to sit at the right-hand end and neither
// is in the designer's chrome; the bell had nothing behind it, and the avatar's only working item
// was Log Out — which now lives at the foot of the sidebar, where a destructive account action is
// out of the way of the page's own controls rather than one click from every screen's header.
export function Topbar({ title, subtitle, backTo, actions }: TopbarProps) {
  const toggleMobileSidebar = useUiStore((s) => s.toggleMobileSidebar);
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-[8] bg-white px-4 py-3 lg:py-[15px] shadow-elevation-1 w-full text-black-logo flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 min-w-0">
        {backTo && (
          <button
            type="button"
            onClick={() => navigate(backTo)}
            aria-label="Back"
            title="Back"
            className="w-5 h-5 lg:w-7 lg:h-7 rounded-full shrink-0 hover:bg-placeholder/15 flex items-center justify-center"
          >
            <ChevronLeft className="w-full h-full" />
          </button>
        )}
        {/* Column so the subtitle sits under the heading rather than beside it.
            `min-w-0` keeps both truncating instead of pushing the right-hand
            controls off a narrow screen. */}
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate text-xl font-bold lg:text-2xl lg:leading-[29px]">{title}</h1>
          {subtitle && (
            <p className="truncate text-[length:var(--text-14)] text-gray66">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 lg:gap-6">
        {/* `min-w-0` is deliberately absent: a search box and a "New …" button must keep their size,
            and it is the heading on the left that truncates when the row runs out of room. */}
        {actions}
        <button
          type="button"
          onClick={toggleMobileSidebar}
          aria-label="Open navigation"
          className="w-5 lg:w-6 h-5 lg:h-6 flex items-center justify-center transition-all duration-300 hover:text-primary lg:hidden"
        >
          <PanelLeft className="w-full h-full" />
        </button>
      </div>
    </nav>
  );
}
